import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  AppStateModel,
  DealerMinimal,
  LookupService,
  Program,
  RemittancePendingContractsRequest,
  RightSidebarService,
  SearchCriteriaPageType,
} from '@mbp/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'mbp-pending-remittance-search-criteria',
  templateUrl: './pending-remittance-search-criteria.component.html',
  styleUrls: ['./pending-remittance-search-criteria.component.scss'],
})
export class PendingRemittanceSearchCriteriaComponent implements OnInit, OnDestroy {
  //#region : Variable Declarations :
  formErrors = {
    dealerCode: '',
  };
  validationMessages = {
    dealerCode: {
      required: 'Please select a dealer',
      dealer: 'Please select a valid dealer',
    },
  };

  isLoading = false;
  searchCriteriaForm: FormGroup;

  programs: Program[] = [];
  private subscription = new Subscription();
  //#endregion

  //#region : Constructor :
  constructor(
    private formBuilder: FormBuilder,
    private store: Store,
    private lookupService: LookupService,
    private rightSidebarService: RightSidebarService,
  ) {}
  //#endregion

  //#region : Properties :
  //#endregion

  //#region : Implementation Methods :
  ngOnInit() {
    this.buildForm();

    setTimeout(() => {
      this.loadLookupData();
    }, 500);

    this.subscription.add(
      this.rightSidebarService.runImmediately$
        .pipe(
          filter((d) => !!d),
          filter(({ searchCriteriaPageType }) => searchCriteriaPageType === SearchCriteriaPageType.PendingRemittance),
          untilDestroyed(this),
        )
        .subscribe((sc) => {
          if (sc.eventData) {
            this.searchCriteriaForm.patchValue({ dealer: sc.eventData });

            const onlyDealer = this.store.selectSnapshot((appState: AppStateModel) => appState.dealer.dealerMinimals.length === 1);
            if (onlyDealer) {
              this.searchCriteriaForm.get('dealer').disable();
              this.searchCriteriaForm.updateValueAndValidity();
            }
          }
          this.search();
        }),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
  //#endregion

  //#region : Methods :
  buildForm() {
    this.searchCriteriaForm = this.formBuilder.group({
      dealer: null,
      programCode: '',
    });
    this.searchCriteriaForm.valueChanges.subscribe((data) => this.onFormValueChanged(data));
  }

  loadLookupData() {
    this.lookupService.getPrograms({}).subscribe(
      (data) => this.onProgamsLoadSuccessful(data),
      (error) => this.onProgramsLoadFailed(error),
    );
  }

  public onSelected(dealer: DealerMinimal) {
    this.searchCriteriaForm.patchValue({ dealerCode: dealer.dealerCode });
    this.rightSidebarService.setDealer(dealer);
  }

  public onFormValueChanged(data?: any) {
    if (!this.searchCriteriaForm) {
      return;
    }
    const form = this.searchCriteriaForm;
    for (const field in this.formErrors) {
      if (Object.prototype.hasOwnProperty.call(this.formErrors, field)) {
        this.formErrors[field] = '';
        const control = form.get(field);
        if (control && control.dirty && !control.valid) {
          const messages = this.validationMessages[field];
          for (const key in control.errors) {
            if (Object.prototype.hasOwnProperty.call(control.errors, key)) {
              this.formErrors[field] += messages[key] + ' ';
            }
          }
        }
      }
    }
  }

  onProgamsLoadSuccessful(programsData: Program[]) {
    this.programs = programsData != null ? programsData : [];
  }

  onProgramsLoadFailed(error: any) {}

  public search() {
    const formValue = this.searchCriteriaForm.getRawValue();
    const searchCriteria = new RemittancePendingContractsRequest();
    searchCriteria.dealer = formValue.dealer;
    searchCriteria.dealerCode = formValue.dealer && formValue.dealer.dealerCode;
    searchCriteria.dealerCode = searchCriteria.dealerCode === '' ? null : searchCriteria.dealerCode;
    searchCriteria.programCode = formValue.programCode;

    this.rightSidebarService.setSearchCriteria(SearchCriteriaPageType.PendingRemittance, searchCriteria);
  }
  //#endregion
}
